import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Alert } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import api from "../api/api";

const cellStyle = (extra = {}) => ({
  border: "1px solid black",
  padding: "3px 7px",
  fontSize: 11,
  ...extra,
});

function HojaVersion({ curso, version, indice, total, empleados, config }) {
  const hoy = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
  const emptyRows = Math.max(5, 20 - empleados.length);

  return (
    <div
      className="hoja-version"
      style={{ padding: "24px", maxWidth: 920, margin: "0 auto", fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      {/* ── Header ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: "2px solid black", marginBottom: 6 }}>
        <tbody>
          <tr>
            {config.company_name && (
              <td style={{ ...cellStyle({ width: 130, textAlign: "center", verticalAlign: "middle", borderRight: "2px solid black", padding: "8px" }) }}>
                <div style={{ fontWeight: "bold", fontSize: 15, lineHeight: 1.3 }}>{config.company_name}</div>
              </td>
            )}
            <td style={{ ...cellStyle({ textAlign: "center", verticalAlign: "middle", padding: "8px" }) }}>
              <div style={{ fontWeight: "bold", fontSize: 20, textTransform: "uppercase", letterSpacing: 1 }}>
                Attendance Sheet
              </div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                New employees — version {indice} of {total}
              </div>
            </td>
            <td style={{ ...cellStyle({ width: 185, verticalAlign: "top", borderLeft: "2px solid black", padding: "7px 10px", fontSize: 11 }) }}>
              {config.attendance_doc_code && <div><strong>Code:</strong> {config.attendance_doc_code}</div>}
              {config.attendance_doc_rev && <div style={{ marginTop: 2 }}><strong>Rev.:</strong> {config.attendance_doc_rev}</div>}
              <div style={{ marginTop: 2 }}><strong>Issue date:</strong> {hoy}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Course and version data ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid black", marginBottom: 6 }}>
        <tbody>
          <tr>
            <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>COURSE / TRAINING:</td>
            <td colSpan={3} style={{ ...cellStyle({ fontWeight: "500" }) }}>{curso.nombre_curso}</td>
          </tr>
          <tr>
            <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>VERSION:</td>
            <td style={{ ...cellStyle() }}>{version.numero_version ?? "—"}</td>
            <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>PUBLICATION DATE:</td>
            <td style={{ ...cellStyle() }}>
              {version.fecha_publicacion
                ? new Date(version.fecha_publicacion + "T00:00:00").toLocaleDateString("en-US")
                : "—"}
            </td>
          </tr>
          <tr>
            <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>DURATION:</td>
            <td style={{ ...cellStyle() }}>
              {version.duracion_horas ? `${version.duracion_horas} hrs` : "—"}
            </td>
            <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>INSTRUCTOR:</td>
            <td style={{ ...cellStyle() }}>{version.instructor || " "}</td>
          </tr>
          {version.descripcion_cambios && (
            <tr>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>CHANGES:</td>
              <td colSpan={3} style={{ ...cellStyle() }}>{version.descripcion_cambios}</td>
            </tr>
          )}
          {version.fichas?.length > 0 && (
            <tr>
              <td style={{ ...cellStyle({ fontWeight: "bold" }) }}>WORKING SHEETS:</td>
              <td colSpan={3} style={{ ...cellStyle() }}>
                {version.fichas.map(f => (
                  <span key={f.codigo} style={{ marginRight: 16, display: "inline-block" }}>
                    <strong>{f.codigo}</strong>
                    {f.version && (
                      <span style={{ color: "#444", marginLeft: 4, fontSize: 10 }}>Rev. {f.version}</span>
                    )}
                    {f.nombre && f.nombre !== f.codigo && (
                      <span style={{ color: "#666", marginLeft: 4 }}>— {f.nombre}</span>
                    )}
                  </span>
                ))}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── Attendees table ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 20 }}>
        <thead>
          <tr style={{ backgroundColor: "#000", color: "white" }}>
            <th style={{ border: "1px solid black", padding: "5px 6px", width: 36, textAlign: "center" }}>No.</th>
            <th style={{ border: "1px solid black", padding: "5px 8px", width: 90, textAlign: "center" }}>PAYROLL #</th>
            <th style={{ border: "1px solid black", padding: "5px 10px", textAlign: "left" }}>FULL NAME</th>
            <th style={{ border: "1px solid black", padding: "5px 8px", width: 150, textAlign: "left" }}>POSITION</th>
            <th style={{ border: "1px solid black", padding: "5px 8px", width: 100, textAlign: "left" }}>AREA</th>
            <th style={{ border: "1px solid black", padding: "5px 8px", width: 120, textAlign: "center" }}>SIGNATURE</th>
          </tr>
        </thead>
        <tbody>
          {empleados.map((emp, i) => (
            <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
              <td style={{ border: "1px solid black", padding: "3px 6px", textAlign: "center" }}>{i + 1}</td>
              <td style={{ border: "1px solid black", padding: "3px 8px", textAlign: "center", fontSize: 10 }}>{emp.numero_nomina ?? "—"}</td>
              <td style={{ border: "1px solid black", padding: "3px 8px" }}>{emp.nombre_completo}</td>
              <td style={{ border: "1px solid black", padding: "3px 8px", fontSize: 10 }}>{emp.puesto_texto}</td>
              <td style={{ border: "1px solid black", padding: "3px 8px", fontSize: 10 }}>{emp.area_texto}</td>
              <td style={{ border: "1px solid black", padding: "3px 8px" }}>&nbsp;</td>
            </tr>
          ))}
          {Array.from({ length: emptyRows }).map((_, i) => (
            <tr key={`empty-${i}`} style={{ height: 22 }}>
              <td style={{ border: "1px solid black", padding: "2px 6px", textAlign: "center", color: "#ccc", fontSize: 10 }}>
                {empleados.length + i + 1}
              </td>
              <td style={{ border: "1px solid black" }}>&nbsp;</td>
              <td style={{ border: "1px solid black" }}>&nbsp;</td>
              <td style={{ border: "1px solid black" }}>&nbsp;</td>
              <td style={{ border: "1px solid black" }}>&nbsp;</td>
              <td style={{ border: "1px solid black" }}>&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── Closing signatures ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 11 }}>
        <tbody>
          <tr>
            {["PREPARED BY", "REVIEWED BY", "APPROVED BY"].map((label) => (
              <td key={label} style={{ width: "33.3%", textAlign: "center", padding: "0 24px", verticalAlign: "bottom" }}>
                <div style={{ borderTop: "1px solid black", paddingTop: 4, marginTop: 44 }}>
                  <strong>{label}</strong>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 12, fontSize: 10, color: "#777", textAlign: "right" }}>
        Total new employees: <strong>{empleados.length}</strong>
      </div>
    </div>
  );
}

export default function ListaAsistenciaVersiones() {
  const { idCurso } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [config, setConfig] = useState({ company_name: "", attendance_doc_code: "", attendance_doc_rev: "" });

  useEffect(() => {
    if (!idCurso) {
      setError("Invalid parameters: missing id_curso.");
      setLoading(false);
      return;
    }
    api
      .get(`/fichas/lista-asistencia-nuevos/${idCurso}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail ?? "Error loading data"))
      .finally(() => setLoading(false));
  }, [idCurso]);

  useEffect(() => {
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }
  if (!data) return null;

  if (data.empleados.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        There are no new employees pending to take this course (all already have a training record).
      </Alert>
    );
  }

  return (
    <>
      <Box
        className="no-print"
        sx={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          bgcolor: "#1565c0", color: "white",
          p: 1.5, display: "flex", justifyContent: "center", gap: 2, alignItems: "center",
          boxShadow: 3,
        }}
      >
        <Button
          variant="contained"
          sx={{ bgcolor: "white", color: "#1565c0", "&:hover": { bgcolor: "#e3f2fd" }, fontWeight: 700 }}
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          Print / Save PDF ({data.versiones.length} versions)
        </Button>
        <Button
          variant="outlined"
          sx={{ borderColor: "rgba(255,255,255,0.7)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
          startIcon={<CloseIcon />}
          onClick={() => window.close()}
        >
          Close
        </Button>
      </Box>

      <div style={{ paddingTop: 72 }}>
        {data.versiones.map((version, idx) => (
          <HojaVersion
            key={version.id_version}
            curso={data.curso}
            version={version}
            indice={idx + 1}
            total={data.versiones.length}
            empleados={data.empleados}
            config={config}
          />
        ))}
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .hoja-version { padding: 0 !important; max-width: 100% !important; break-after: page; }
          .hoja-version:last-child { break-after: auto; }
          @page { margin: 10mm; size: A4 portrait; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; }
        }
      `}</style>
    </>
  );
}
